import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createAnalyticsApi } from "../api";
import {
  classificationTone,
  formatLabel,
  moduleDrilldownPath,
  recordStatusTone,
} from "../labels";
import type { SavedViewDto, UserSegmentDto } from "../types";

export function SavedViewDetailPage() {
  const { viewId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createAnalyticsApi(client), [client]);

  const [row, setRow] = useState<SavedViewDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Analytics", to: "/app/analytics" },
    { label: "Saved views", to: "/app/analytics/saved-views" },
    { label: row?.name ?? "Saved view" },
  ]);

  const load = useCallback(async () => {
    if (!viewId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getSavedView(viewId);
      setRow(result.saved_view);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, viewId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading saved view…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Saved view unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const drilldown = moduleDrilldownPath(row.module);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={row.name}
        description={`Module: ${row.module}`}
        actions={
          drilldown ? (
            <Button
              variant="secondary"
              onClick={() => void navigate(drilldown)}
            >
              Open module queue
            </Button>
          ) : null
        }
      />

      <ActionBar
        left={
          <StatusChip tone={recordStatusTone(row.status)}>
            {formatLabel(row.status)}
          </StatusChip>
        }
      />

      <Alert tone="info" title="Definition scope">
        This is a persisted view definition (filters/sort), not a computed
        report. Analytics does not run the filter_definition against live data
        or invent chart series.
      </Alert>

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Record
          </Text>
          <Text role="body" as="p">
            ID: {row.id}
          </Text>
          <Text role="body" as="p">
            Owner User ID: {row.owner_user_id}
          </Text>
          <Text role="body" as="p">
            Visibility scope: {row.visibility_scope ?? "—"}
          </Text>
          <Text role="body" as="p">
            Shared with scope: {row.shared_with_scope ?? "—"}
          </Text>
          <Text role="body" as="p">
            Created at: {row.created_at}
          </Text>
          {!drilldown ? (
            <Text role="caption" color="muted" as="p">
              Module &quot;{row.module}&quot; has no mapped HQ drill-down path.
            </Text>
          ) : null}
        </Stack>
      </Card>

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            filter_definition
          </Text>
          <Text role="caption" color="muted" as="p">
            Opaque JSON from the API.
          </Text>
          <Text role="body" as="p">
            {JSON.stringify(row.filter_definition)}
          </Text>
        </Stack>
      </Card>

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            sort_definition
          </Text>
          <Text role="caption" color="muted" as="p">
            Opaque JSON from the API.
          </Text>
          <Text role="body" as="p">
            {JSON.stringify(row.sort_definition)}
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

export function SegmentDetailPage() {
  const { segmentId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createAnalyticsApi(client), [client]);

  const [row, setRow] = useState<UserSegmentDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Analytics", to: "/app/analytics" },
    { label: "Segments", to: "/app/analytics/segments" },
    { label: row?.name ?? "Segment" },
  ]);

  const load = useCallback(async () => {
    if (!segmentId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getUserSegment(segmentId);
      setRow(result.user_segment);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, segmentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading segment…" />;
  if (error || !row) {
    return (
      <EmptyState
        title="Segment unavailable"
        description={error ?? "Not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title={row.name}
        description="Saved user segment definition"
        actions={
          <Button
            variant="secondary"
            onClick={() => void navigate("/app/users")}
          >
            Open Users directory
          </Button>
        }
      />

      <ActionBar
        left={
          <Flex gap={8} wrap>
            <StatusChip tone={recordStatusTone(row.status)}>
              {formatLabel(row.status)}
            </StatusChip>
            <StatusChip tone={classificationTone(row.classification)}>
              {formatLabel(row.classification)}
            </StatusChip>
          </Flex>
        }
      />

      <Alert tone="warning" title="Sensitive segment criteria">
        criteria_definition is shown as stored. Membership is not expanded
        here. Users directory remains the operational surface for individual
        accounts.
      </Alert>

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            Record
          </Text>
          <Text role="body" as="p">
            ID: {row.id}
          </Text>
          <Text role="body" as="p">
            Owner User ID: {row.owner_user_id}
          </Text>
          <Text role="body" as="p">
            Shared with scope: {row.shared_with_scope ?? "—"}
          </Text>
          <Text role="body" as="p">
            Created at: {row.created_at}
          </Text>
        </Stack>
      </Card>

      <Card>
        <Stack gap={8}>
          <Text role="h2" as="h2">
            criteria_definition
          </Text>
          <Text role="caption" color="muted" as="p">
            Opaque JSON from the API — not evaluated as a live query.
          </Text>
          <Text role="body" as="p">
            {JSON.stringify(row.criteria_definition)}
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
